import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const TenderDetailsModal = ({ requisition, onClose, onSaved }) => {
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("emd");

  const [form, setForm] = useState({
    emd_applicable: requisition.emd_applicable ?? null,
    emd_amount: requisition.emd_amount || "",
    emd_form_type: requisition.emd_form_type || "",
    emd_due_date: requisition.emd_due_date || "",
    emd_tender_reference: requisition.emd_tender_reference || "",
    emd_exemption_applicable: requisition.emd_exemption_applicable ?? null,
    emd_exemption_criteria: requisition.emd_exemption_criteria || "",
    aps_applicable: requisition.aps_applicable ?? null,
    aps_instrument_type: requisition.aps_instrument_type || "",
    aps_pbg_reference: requisition.aps_pbg_reference || "",
    aps_amount: requisition.aps_amount || "",
    aps_issue_date: requisition.aps_issue_date || "",
    aps_expiry_date: requisition.aps_expiry_date || "",
    aps_vendor_name: requisition.aps_vendor_name || "",
    aps_bank_name: requisition.aps_bank_name || "",
    aps_branch_name: requisition.aps_branch_name || "",
    aps_ifsc_code: requisition.aps_ifsc_code || "",
    aps_remarks: requisition.aps_remarks || "",
    integrity_pact_applicable: requisition.integrity_pact_applicable ?? null,
    integrity_pact_reference: requisition.integrity_pact_reference || "",
    integrity_pact_signing_date: requisition.integrity_pact_signing_date || "",
    integrity_pact_expiry_date: requisition.integrity_pact_expiry_date || "",
    integrity_pact_vendor_name: requisition.integrity_pact_vendor_name || "",
    integrity_pact_tender_number: requisition.integrity_pact_tender_number || "",
    integrity_pact_po_number: requisition.integrity_pact_po_number || "",
    integrity_pact_remarks: requisition.integrity_pact_remarks || "",
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/requisitions/${requisition.id}/tender-details`, form, getAuthHeader());
      toast.success("Tender details saved");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: "emd", label: "EMD", color: "rose" },
    { key: "aps", label: "APS / Performance Security", color: "teal" },
    { key: "integrity", label: "Integrity Pact", color: "indigo" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 mb-8" onClick={e => e.stopPropagation()} data-testid="tender-details-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Tender Details</h2>
            <p className="text-sm text-slate-500">{requisition.requisition_number}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="close-tender-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6 gap-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? `border-${t.color}-600 text-${t.color}-700`
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* EMD Tab */}
          {activeTab === "emd" && (
            <div className="space-y-5" data-testid="emd-section">
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm font-semibold text-slate-700 mb-3">Is EMD (Earnest Money Deposit) applicable?</p>
                <div className="flex gap-4 ml-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.emd_applicable === true} onChange={() => set("emd_applicable", true)} />
                    <span className="text-sm font-medium">YES</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.emd_applicable === false} onChange={() => set("emd_applicable", false)} />
                    <span className="text-sm font-medium">NO</span>
                  </label>
                </div>
              </div>

              {form.emd_applicable === true && (
                <div className="p-4 bg-white rounded-lg border border-rose-200 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">EMD Amount</Label>
                      <Input type="number" value={form.emd_amount} onChange={e => set("emd_amount", e.target.value)} className="mt-1" data-testid="emd-amount" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">EMD Form / Type</Label>
                      <select value={form.emd_form_type} onChange={e => set("emd_form_type", e.target.value)} className="w-full mt-1 p-2 border rounded-md text-sm" data-testid="emd-form-type">
                        <option value="">Select</option>
                        <option value="BG">Bank Guarantee (BG)</option>
                        <option value="DD">Demand Draft (DD)</option>
                        <option value="FDR">Fixed Deposit Receipt (FDR)</option>
                        <option value="ONLINE">Online Payment</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">EMD Due Date</Label>
                      <Input type="date" value={form.emd_due_date} onChange={e => set("emd_due_date", e.target.value)} className="mt-1" data-testid="emd-due-date" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tender Document Reference</Label>
                    <Input value={form.emd_tender_reference} onChange={e => set("emd_tender_reference", e.target.value)} className="mt-1" placeholder="Clause reference" />
                  </div>
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm font-semibold text-slate-700 mb-3">Is EMD Exemption applicable?</p>
                <div className="flex gap-4 ml-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.emd_exemption_applicable === true} onChange={() => set("emd_exemption_applicable", true)} />
                    <span className="text-sm font-medium">YES</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.emd_exemption_applicable === false} onChange={() => set("emd_exemption_applicable", false)} />
                    <span className="text-sm font-medium">NO</span>
                  </label>
                </div>
                {form.emd_exemption_applicable === true && (
                  <div className="mt-3 ml-4">
                    <Label className="text-xs text-slate-500">Exemption Criteria</Label>
                    <select value={form.emd_exemption_criteria} onChange={e => set("emd_exemption_criteria", e.target.value)} className="w-full mt-1 p-2 border rounded-md text-sm max-w-xs">
                      <option value="">Select</option>
                      <option value="MSE">Micro/Small Enterprise (MSE)</option>
                      <option value="STARTUP">Start-up (DPIIT recognized)</option>
                      <option value="GOVT">Government Organization</option>
                      <option value="OTHERS">Others</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* APS Tab */}
          {activeTab === "aps" && (
            <div className="space-y-5" data-testid="aps-section">
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm font-semibold text-slate-700 mb-3">Is Advance Performance Security applicable?</p>
                <div className="flex gap-4 ml-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.aps_applicable === true} onChange={() => set("aps_applicable", true)} />
                    <span className="text-sm font-medium">YES</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.aps_applicable === false} onChange={() => set("aps_applicable", false)} />
                    <span className="text-sm font-medium">NO</span>
                  </label>
                </div>
              </div>

              {form.aps_applicable === true && (
                <div className="p-4 bg-white rounded-lg border border-teal-200 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Instrument Type</Label>
                      <select value={form.aps_instrument_type} onChange={e => set("aps_instrument_type", e.target.value)} className="w-full mt-1 p-2 border rounded-md text-sm" data-testid="aps-instrument-type">
                        <option value="">Select</option>
                        <option value="DD">DD</option>
                        <option value="BG">BG</option>
                        <option value="FDR">FDR</option>
                        <option value="ONLINE">Online</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">PBG Reference Number</Label>
                      <Input value={form.aps_pbg_reference} onChange={e => set("aps_pbg_reference", e.target.value)} className="mt-1" data-testid="aps-pbg-ref" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">PBG Amount</Label>
                      <Input type="number" value={form.aps_amount} onChange={e => set("aps_amount", e.target.value)} className="mt-1" data-testid="aps-amount" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Date of Issue</Label>
                      <Input type="date" value={form.aps_issue_date} onChange={e => set("aps_issue_date", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Expiry Date</Label>
                      <Input type="date" value={form.aps_expiry_date} onChange={e => set("aps_expiry_date", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Vendor Name</Label>
                      <Input value={form.aps_vendor_name} onChange={e => set("aps_vendor_name", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Bank Name</Label>
                      <Input value={form.aps_bank_name} onChange={e => set("aps_bank_name", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Branch Name</Label>
                      <Input value={form.aps_branch_name} onChange={e => set("aps_branch_name", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">IFSC Code</Label>
                      <Input value={form.aps_ifsc_code} onChange={e => set("aps_ifsc_code", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Remarks</Label>
                    <Textarea value={form.aps_remarks} onChange={e => set("aps_remarks", e.target.value)} className="mt-1" rows={2} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Integrity Pact Tab */}
          {activeTab === "integrity" && (
            <div className="space-y-5" data-testid="integrity-section">
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm font-semibold text-slate-700 mb-3">Is Integrity Pact applicable?</p>
                <div className="flex gap-4 ml-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.integrity_pact_applicable === true} onChange={() => set("integrity_pact_applicable", true)} />
                    <span className="text-sm font-medium">YES</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.integrity_pact_applicable === false} onChange={() => set("integrity_pact_applicable", false)} />
                    <span className="text-sm font-medium">NO</span>
                  </label>
                </div>
              </div>

              {form.integrity_pact_applicable === true && (
                <div className="p-4 bg-white rounded-lg border border-indigo-200 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Reference Number</Label>
                      <Input value={form.integrity_pact_reference} onChange={e => set("integrity_pact_reference", e.target.value)} className="mt-1" data-testid="ip-reference" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Date of Signing</Label>
                      <Input type="date" value={form.integrity_pact_signing_date} onChange={e => set("integrity_pact_signing_date", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Validity Expiry Date</Label>
                      <Input type="date" value={form.integrity_pact_expiry_date} onChange={e => set("integrity_pact_expiry_date", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Vendor Name</Label>
                      <Input value={form.integrity_pact_vendor_name} onChange={e => set("integrity_pact_vendor_name", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Tender Number</Label>
                      <Input value={form.integrity_pact_tender_number} onChange={e => set("integrity_pact_tender_number", e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">PO Number</Label>
                      <Input value={form.integrity_pact_po_number} onChange={e => set("integrity_pact_po_number", e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Remarks</Label>
                    <Textarea value={form.integrity_pact_remarks} onChange={e => set("integrity_pact_remarks", e.target.value)} className="mt-1" rows={2} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="cancel-tender-details">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-tender-details">
            {saving ? "Saving..." : "Save Details"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenderDetailsModal;
